/**
 * AdminDanismanAnaSayfa.jsx
 *
 * Danışman Ana Sayfası — danışmanın login sonrası gördüğü ilk ekran.
 * Route: /danisman
 * Erişim: danisman, admin, superadmin
 *
 * Backend: routes/danisman_cockpit.py (+ routes/ciro_dashboard.py)
 *   GET  /danisman/ciro-dashboard/danisman-satislari?date=|month=  (Blok 1)
 *   GET  /danisman/cockpit/bugun-satislar?tarih=                   (Blok 2a)
 *   POST /danisman/cockpit/mesai-basla                             (Blok 2b, mount'ta bir kez)
 *   GET  /danisman/cockpit/mesai-listesi?tarih=                    (Blok 2b)
 *   GET  /danisman/cockpit/odemesi-gelenler?limit=50                (Blok 3a)
 *   GET  /danisman/cockpit/summary                                  (Blok 3a — yalnız yaklasan_odemeler rozeti, Faz D)
 *   GET  /danisman/cockpit/gun-sonu-raporu                          (Blok 3b — yalnız danışman)
 *   POST /danisman/cockpit/gun-sonu-raporu                          (Blok 3b — yalnız danışman, admin 403)
 *
 * Blok 2c (Değerlendirmeler) veri kaynağı YOK — fetch yapılmaz, sabit
 * dürüst boş durum gösterilir (bkz. panel içi yorum).
 *
 * Rol bazlı şekillendirme: admin/superadmin ek "danışman" bilgisi (chip)
 * görür, danışman kendi verisini sıra/oran olmadan görür (bkz. her panelin
 * kendi yorumu).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import ErrorBoundary, { BlockErrorFallback } from '../components/ErrorBoundary';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { fmtMoney } from '../utils/money';
import { rootBreadcrumb } from '../utils/adminBreadcrumbs';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  useLeadStatusOptions, leadStatusColors, leadStatusLabel,
} from '../hooks/useLeadStatusOptions';
import { NavControl, KpiCard, todayStr, shiftDate } from '../components/crm/CrmKpiPrimitives';
import { DanismanSatislariCards } from '../components/crm/DanismanSatislariPanels';
import Pagination from '../components/crm/Pagination';
import {
  ShoppingCart, Users2, Star, CreditCard, BarChart3, AlertTriangle, RefreshCw, Share2, ChevronDown, ChevronUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const YORUM_MAX = 3000;

// ─── Ortak yardımcılar ──────────────────────────────────────────────────────

/** ISO tarih-saat string'inden yalnızca HH:mm döner; zaten "HH:mm" ise aynen döner. */
function fmtSaat(v) {
  if (!v) return '--:--';
  const s = String(v);
  if (/^\d{1,2}:\d{2}$/.test(s)) return s;
  try {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    }
  } catch { /* düşer, ham değeri döner */ }
  return s;
}

/** fmtSaat gibi ancak boş/yok değerde '–' döner (admin gün sonu raporları listesi için). */
function fmtSaatOrDash(v) {
  return v ? fmtSaat(v) : '–';
}

function fmtSavedAt(iso, lang) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
}

function CardShell({ children, className = '' }) {
  return (
    <div className={`bg-white p-4 ${className}`} style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
      {children}
    </div>
  );
}

function ErrorRetry({ onRetry, tr }) {
  return (
    <div
      className="p-3 rounded-xl flex items-center justify-between gap-3"
      style={{ backgroundColor: 'rgba(160,61,44,0.06)', border: '1px solid rgba(160,61,44,0.25)' }}
    >
      <span className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--crm-danger)' }}>
        <AlertTriangle size={16} /> {tr('Bağlantı koptu — tekrar dene', 'Connection lost — try again')}
      </span>
      <button
        onClick={onRetry}
        className="px-3 py-1.5 rounded-lg text-xs font-bold text-white flex items-center gap-1 flex-shrink-0"
        style={{ backgroundColor: 'var(--crm-danger)' }}
      >
        <RefreshCw size={13} /> {tr('Tekrar Dene', 'Retry')}
      </button>
    </div>
  );
}

// ─── Blok 2a — Bugün Satışlar ───────────────────────────────────────────────

function BugunSatislarPanel({ authHeaders, lang, tr, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await axios.get(`${API}/danisman/cockpit/bugun-satislar?tarih=${todayStr()}`, { headers: authHeaders() });
      setItems(r.data.items || []);
    } catch (err) { setError(true); toast.error(getApiErrorMessage(err, lang)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <CardShell>
      <h3 className="font-bold text-sm flex items-center gap-1.5 mb-3" style={{ color: 'var(--crm-navy-text)' }}>
        <ShoppingCart size={15} style={{ color: 'var(--crm-navy)' }} /> {tr('Bugün Satışlar', "Today's Sales")}
      </h3>
      {loading && <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>}
      {!loading && error && <ErrorRetry onRetry={fetchData} tr={tr} />}
      {!loading && !error && items.length === 0 && (
        <p className="text-xs" style={{ color: 'var(--crm-muted)' }}>{tr('Bugün henüz satış yok.', 'No sales yet today.')}</p>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded-lg text-sm flex-wrap" style={{ backgroundColor: 'var(--crm-cream)' }}>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600 flex-shrink-0">
                {fmtSaat(it.saat)}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate" style={{ color: 'var(--crm-navy-text)' }}>{it.ogrenci_adi}</p>
                <p className="text-xs truncate" style={{ color: 'var(--crm-muted)' }}>{it.egitim}</p>
              </div>
              {isAdmin && it.danisman_adi && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: 'white', color: 'var(--crm-muted)', border: '1px solid var(--crm-card-border)' }}>
                  {it.danisman_adi}
                </span>
              )}
              <span className="text-xs font-bold flex-shrink-0" style={{ color: 'var(--crm-navy-text)' }}>{fmtMoney(it.tutar)}</span>
            </div>
          ))}
        </div>
      )}
    </CardShell>
  );
}

// ─── Blok 2b — Mesai Başlangıç ──────────────────────────────────────────────

function MesaiPanel({ authHeaders, lang, tr }) {
  const [date, setDate] = useState(todayStr());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await axios.get(`${API}/danisman/cockpit/mesai-listesi?tarih=${date}`, { headers: authHeaders() });
      setItems(r.data.items || []);
    } catch (err) { setError(true); toast.error(getApiErrorMessage(err, lang)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <CardShell>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="font-bold text-sm flex items-center gap-1.5" style={{ color: 'var(--crm-navy-text)' }}>
          <Users2 size={15} style={{ color: 'var(--crm-navy)' }} /> {tr('Mesai Başlangıç', 'Shift Start')}
        </h3>
        <NavControl
          value={date}
          onPrev={() => setDate((d) => shiftDate(d, -1))}
          onNext={() => setDate((d) => shiftDate(d, 1))}
          nextDisabled={date >= todayStr()}
          tr={tr}
        />
      </div>
      {loading && <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>}
      {!loading && error && <ErrorRetry onRetry={fetchData} tr={tr} />}
      {!loading && !error && items.length === 0 && (
        <p className="text-xs" style={{ color: 'var(--crm-muted)' }}>
          {tr('Bugün henüz kimse mesaiye başlamadı.', 'No one has started their shift today yet.')}
        </p>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {items.map((it, i) => (
            <div key={it.user_id || i} className="flex items-center gap-2 py-1.5 px-2 rounded-lg text-sm" style={{ backgroundColor: 'var(--crm-cream)' }}>
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 ${it.bitis ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'}`}
              >
                {fmtSaat(it.baslangic)}
              </span>
              <span className="flex-1 font-semibold truncate" style={{ color: 'var(--crm-navy-text)' }}>{it.name}</span>
              {it.bitis && (
                <span className="text-[10px] font-bold flex-shrink-0" style={{ color: 'var(--crm-muted)' }}>{fmtSaat(it.bitis)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </CardShell>
  );
}

// ─── Blok 2c — Değerlendirmeler (veri kaynağı yok) ─────────────────────────

function DegerlendirmelerPanel({ tr }) {
  return (
    <CardShell>
      <h3 className="font-bold text-sm flex items-center gap-1.5 mb-3" style={{ color: 'var(--crm-navy-text)' }}>
        <Star size={15} style={{ color: 'var(--crm-navy)' }} /> {tr('Değerlendirmeler', 'Reviews')}
      </h3>
      <p className="text-xs" style={{ color: 'var(--crm-muted)' }}>
        {tr(
          "Görüşme sonrası öğrencilere gönderilen değerlendirme SMS'lerine gelen cevaplar burada listelenecek. Bu entegrasyon henüz bağlı değil.",
          "Replies to the post-call review SMS sent to students will appear here. This integration isn't connected yet."
        )}
      </p>
    </CardShell>
  );
}

// ─── Blok 3a — Ödemesi Gelenler ─────────────────────────────────────────────

// Faz D — satır etiketi (bkz. routes/danisman_cockpit.py::_odeme_pencere_durumu).
// Bilinmeyen/eksik değer güvenli varsayılan olarak "gecikmis" stiline düşer.
const PENCERE_DURUMU_ROW_STYLES = {
  gecikmis: { bg: 'bg-rose-50', badgeBg: 'bg-rose-100', badgeText: 'text-rose-700' },
  bugun: { bg: 'bg-amber-50', badgeBg: 'bg-amber-100', badgeText: 'text-amber-700' },
  yaklasan: { bg: '', badgeBg: 'bg-cyan-100', badgeText: 'text-cyan-700' },
};

function OdemesiGelenlerPanel({ authHeaders, lang, tr, isAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(50);
  // Faz D (ADDITIVE, minimal): summary'deki yaklaşan (7 gün) ödeme sayacı —
  // yalnızca küçük bir rozet için, listenin kendisi hâlâ eski davranışla
  // (gecikmiş+bugün, pencere param'sız) çekiliyor (bkz. cockpit_summary
  // yaklasan_odemeler). Sessiz best-effort — hata olursa rozet gizlenir.
  const [yaklasanAdet, setYaklasanAdet] = useState(null);
  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await axios.get(
        `${API}/danisman/cockpit/odemesi-gelenler?limit=${limit}&offset=${offset}`,
        { headers: authHeaders() }
      );
      setData(r.data);
    } catch (err) { setError(true); toast.error(getApiErrorMessage(err, lang)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, offset, lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/danisman/cockpit/summary`, { headers: authHeaders() })
      .then((r) => { if (!cancelled) setYaklasanAdet(r.data?.yaklasan_odemeler?.adet ?? 0); })
      .catch(() => { if (!cancelled) setYaklasanAdet(null); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = data?.items || [];
  // DİKKAT: `data.adet`/`data.odenmeyen_toplam` yalnızca DÖNEN SAYFANIN
  // toplamıdır — gerçek toplam kayıt sayısı `data.total`'dır (bkz. backend
  // routes/danisman_cockpit.py::cockpit_odemesi_gelenler docstring'i).
  // Rozet toplam kaydı göstermeli, sayfa boyutunu değil.
  const total = data?.total ?? 0;

  return (
    <CardShell>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-sm flex items-center gap-1.5" style={{ color: 'var(--crm-navy-text)' }}>
          <CreditCard size={15} style={{ color: 'var(--crm-navy)' }} /> {tr('Ödemesi Gelenler', 'Upcoming Payments')}
        </h3>
        <div className="flex items-center gap-1.5">
          {!loading && !error && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: 'var(--crm-navy)' }}>
              {total}
            </span>
          )}
          {/* Faz D: yaklaşan (7 gün) sayacı — additive, minimal rozet */}
          {yaklasanAdet !== null && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-cyan-100 text-cyan-700">
              {tr(`Yaklaşan: ${yaklasanAdet}`, `Upcoming: ${yaklasanAdet}`)}
            </span>
          )}
        </div>
      </div>
      {loading && <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>}
      {!loading && error && <ErrorRetry onRetry={fetchData} tr={tr} />}
      {!loading && !error && items.length === 0 && (
        <p className="text-xs" style={{ color: 'var(--crm-muted)' }}>{tr('Yaklaşan/eskiyen ödeme yok.', 'No upcoming or overdue payments.')}</p>
      )}
      {!loading && !error && items.length > 0 && (
        <>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {items.map((it, i) => {
              const gecikmis = Number(it.gecikme_gun || 0) > 0;
              // Faz D: satır rozeti artık pencere_durumu'na göre (gecikmis/
              // bugun/yaklasan) — bu liste hâlâ eski çağrıyla (gecikmiş+bugün)
              // çekildiği için pratikte yaklasan hiç dönmez, ama alan güvenli
              // varsayılanla (gecikmis) ele alınır.
              const durum = PENCERE_DURUMU_ROW_STYLES[it.pencere_durumu] ? it.pencere_durumu : (gecikmis ? 'gecikmis' : 'bugun');
              const style = PENCERE_DURUMU_ROW_STYLES[durum];
              return (
                <div
                  key={i}
                  className={`flex items-center gap-2 py-1.5 px-2 rounded-lg text-sm flex-wrap ${style.bg}`}
                  style={!style.bg ? { backgroundColor: 'var(--crm-cream)' } : undefined}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate" style={{ color: 'var(--crm-navy-text)' }}>{it.ogrenci_adi}</p>
                  </div>
                  {isAdmin && it.danisman_adi && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: 'white', color: 'var(--crm-muted)', border: '1px solid var(--crm-card-border)' }}>
                      {it.danisman_adi}
                    </span>
                  )}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 ${style.badgeBg} ${style.badgeText}`}>
                    {it.due_date}{gecikmis ? ` · ${it.gecikme_gun} ${tr('gün gecikmiş', 'days overdue')}` : ''}
                  </span>
                  <span className="text-xs font-bold flex-shrink-0" style={{ color: 'var(--crm-navy-text)' }}>{fmtMoney(it.amount)}</span>
                </div>
              );
            })}
          </div>
          <Pagination
            total={total}
            limit={limit}
            offset={offset}
            loading={loading}
            tr={tr}
            pageSizeOptions={[50, 100, 200]}
            onPageChange={(o) => setOffset(o)}
            onLimitChange={(s) => { setLimit(s); setOffset(0); }}
          />
        </>
      )}
    </CardShell>
  );
}

// ─── Blok 3b — Gün Sonu Raporu (admin/superadmin — tüm danışmanlar, salt okunur) ──

const YORUM_KIRPMA_ESIGI = 220; // ~3-4 satır karşılığı kaba karakter eşiği

function AdminGunSonuRaporlariPanel({ authHeaders, lang, tr }) {
  const toast = useToast();
  const [date, setDate] = useState(todayStr());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await axios.get(`${API}/danisman/cockpit/gun-sonu-raporlari?tarih=${date}`, { headers: authHeaders() });
      setItems(r.data.items || []);
    } catch (err) { setError(true); toast.error(getApiErrorMessage(err, lang)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExpanded = (userId) => setExpanded((e) => ({ ...e, [userId]: !e[userId] }));

  return (
    <CardShell>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="font-bold text-sm flex items-center gap-1.5" style={{ color: 'var(--crm-navy-text)' }}>
          <BarChart3 size={15} style={{ color: 'var(--crm-navy)' }} /> {tr('Gün Sonu Raporu', 'End of Day Report')}
        </h3>
        <NavControl
          value={date}
          onPrev={() => setDate((d) => shiftDate(d, -1))}
          onNext={() => setDate((d) => shiftDate(d, 1))}
          nextDisabled={date >= todayStr()}
          tr={tr}
        />
      </div>

      {loading && <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>}
      {!loading && error && <ErrorRetry onRetry={fetchData} tr={tr} />}
      {!loading && !error && items.length === 0 && (
        <p className="text-xs" style={{ color: 'var(--crm-muted)' }}>
          {tr('Bu gün için rapor yazılmamış.', 'No reports were submitted for this day.')}
        </p>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {items.map((it) => {
            const isExpanded = !!expanded[it.user_id];
            const yorum = it.yorum || '';
            const needsClamp = yorum.length > YORUM_KIRPMA_ESIGI;
            return (
              <div key={it.user_id} className="p-2.5 rounded-lg" style={{ backgroundColor: 'var(--crm-cream)' }}>
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                  <span className="font-bold text-sm" style={{ color: 'var(--crm-navy-text)' }}>{it.danisman_adi}</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-semibold" style={{ color: 'var(--crm-muted)' }}>
                      {fmtSaatOrDash(it.mesai_baslangic)}–{fmtSaatOrDash(it.mesai_bitis)}
                    </span>
                    {it.istatistikleri_gonder && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                        style={{ backgroundColor: 'rgba(29,138,76,0.12)', color: 'var(--crm-success)' }}
                      >
                        <Share2 size={10} /> {tr('İstatistik paylaşıldı', 'Stats shared')}
                      </span>
                    )}
                  </div>
                </div>
                <p
                  className={`text-xs whitespace-pre-wrap ${!isExpanded && needsClamp ? 'line-clamp-3' : ''}`}
                  style={{ color: 'var(--crm-navy-text)' }}
                >
                  {yorum}
                </p>
                {needsClamp && (
                  <button
                    onClick={() => toggleExpanded(it.user_id)}
                    className="text-[11px] font-bold mt-1 flex items-center gap-0.5"
                    style={{ color: 'var(--crm-info)' }}
                  >
                    {isExpanded ? tr('Daha az göster', 'Show less') : tr('Devamını gör', 'Show more')}
                    {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </CardShell>
  );
}

// ─── Blok 3b — Gün Sonu Raporu ───────────────────────────────────────────────

function GunSonuRaporuPanel({ authHeaders, lang, tr, isDanisman }) {
  const toast = useToast();
  const { options: statusOptions } = useLeadStatusOptions();

  const [rapor, setRapor] = useState(null);
  const [loading, setLoading] = useState(isDanisman);
  const [error, setError] = useState(false);

  const [yorum, setYorum] = useState('');
  const [istatistikleriGonder, setIstatistikleriGonder] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await axios.get(`${API}/danisman/cockpit/gun-sonu-raporu`, { headers: authHeaders() });
      setRapor(r.data);
      const kayitli = r.data?.kayitli_rapor;
      if (kayitli) {
        setYorum(kayitli.yorum || '');
        setIstatistikleriGonder(!!kayitli.istatistikleri_gonder);
      }
    } catch (err) { setError(true); toast.error(getApiErrorMessage(err, lang)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (isDanisman) fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDanisman]);

  if (!isDanisman) {
    return <AdminGunSonuRaporlariPanel authHeaders={authHeaders} lang={lang} tr={tr} />;
  }

  const kayitliRapor = rapor?.kayitli_rapor || null;
  const durumDagilimi = Object.entries(rapor?.durum_dagilimi || {});
  const yorumValid = yorum.trim().length > 0 && yorum.length <= YORUM_MAX;

  const doSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/danisman/cockpit/gun-sonu-raporu`,
        { yorum: yorum.trim(), istatistikleri_gonder: istatistikleriGonder },
        { headers: authHeaders() }
      );
      toast.success(tr('Gün sonu raporu kaydedildi.', 'End-of-day report saved.'));
      setConfirmOpen(false);
      // Kayıt sonrası güncel kayitli_rapor + agregasyonu göstermek için tekrar çek
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (!yorumValid) return;
    if (kayitliRapor) {
      doSave();
    } else {
      setConfirmOpen(true);
    }
  };

  return (
    <CardShell>
      <h3 className="font-bold text-sm flex items-center gap-1.5 mb-3" style={{ color: 'var(--crm-navy-text)' }}>
        <BarChart3 size={15} style={{ color: 'var(--crm-navy)' }} /> {tr('Gün Sonu Raporu', 'End of Day Report')}
      </h3>

      {loading && (
        <div className="space-y-3 mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        </div>
      )}
      {!loading && error && <div className="mb-4"><ErrorRetry onRetry={fetchData} tr={tr} /></div>}
      {!loading && !error && rapor && (
        <div className="mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            <KpiCard label={tr('Toplam Görüşme', 'Total Calls')} value={rapor.toplam_gorusme ?? 0} />
            <KpiCard label={tr('Hatırlatma', 'Reminders')} value={rapor.hatirlatma_olusturulan ?? 0} />
            <KpiCard label={tr('Durum Kaydı', 'Status Records')} value={durumDagilimi.reduce((a, [, c]) => a + c, 0)} />
          </div>
          {durumDagilimi.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {durumDagilimi.map(([slug, count]) => {
                const { bg, text, border } = leadStatusColors(slug);
                const lbl = leadStatusLabel(slug, statusOptions);
                return (
                  <span key={slug} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${bg} ${text} ${border}`}>
                    {lbl} <span className="px-1 py-0.5 rounded-full bg-white/60">{count}</span>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {kayitliRapor && (
        <div className="mb-3 p-2.5 rounded-lg text-xs font-semibold flex items-center justify-between gap-2 flex-wrap" style={{ backgroundColor: 'rgba(29,138,76,0.08)', color: 'var(--crm-success)' }}>
          <span>{tr('Bugünkü raporunuz kaydedildi.', 'Your report for today was saved.')}</span>
          <span className="font-normal opacity-80">{fmtSavedAt(kayitliRapor.updated_at || kayitliRapor.created_at, lang)}</span>
        </div>
      )}

      <div className="mb-1">
        <label className="text-[11px] font-bold uppercase tracking-wide block mb-1" style={{ color: 'var(--crm-muted-2)' }}>
          {tr('Yorumun', 'Your note')}
        </label>
        <textarea
          value={yorum}
          onChange={(e) => setYorum(e.target.value.slice(0, YORUM_MAX))}
          maxLength={YORUM_MAX}
          className="w-full px-3 py-2 rounded-lg border text-sm resize-none focus:outline-none focus:ring-2"
          style={{ borderColor: 'var(--crm-input-border)', color: 'var(--crm-navy-text)', minHeight: 120 }}
          placeholder={tr('Bugün öne çıkan görüşmeler, notlar...', 'Notable calls, notes for today...')}
        />
        <p className="text-[11px] font-semibold mt-1 text-right" style={{ color: 'var(--crm-muted-2)' }}>
          {yorum.length} / {YORUM_MAX}
        </p>
      </div>

      <label className="flex items-center gap-2 text-xs font-semibold mb-3 cursor-pointer" style={{ color: 'var(--crm-navy-text)' }}>
        <input
          type="checkbox"
          checked={istatistikleriGonder}
          onChange={(e) => setIstatistikleriGonder(e.target.checked)}
        />
        {tr('İstatistikleri yöneticimle paylaş', 'Share statistics with my manager')}
      </label>

      <button
        onClick={handleSaveClick}
        disabled={!yorumValid || saving}
        className="px-4 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-50"
        style={{ backgroundColor: 'var(--crm-navy)' }}
      >
        {saving ? '...' : (kayitliRapor ? tr('Güncelle', 'Update') : tr('Kaydet ve Günü Kapat', 'Save & End Shift'))}
      </button>
      {!kayitliRapor && (
        <p className="text-[11px] font-semibold mt-2" style={{ color: 'var(--crm-muted)' }}>
          {tr('Bu raporu kaydetmek bugünkü mesainizi sonlandırır.', 'Saving this report ends your shift for today.')}
        </p>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!saving) setConfirmOpen(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">{tr('Mesaiyi sonlandır?', 'End your shift?')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'var(--crm-muted)' }}>
            {tr('Bu raporu kaydetmek bugünkü mesainizi sonlandırır.', 'Saving this report ends your shift for today.')}
          </p>
          <DialogFooter>
            <button
              onClick={() => setConfirmOpen(false)}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-100"
            >
              {tr('Vazgeç', 'Cancel')}
            </button>
            <button
              onClick={doSave}
              disabled={saving}
              className="px-5 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: 'var(--crm-navy)' }}
            >
              {saving ? '...' : tr('Evet, Kaydet', 'Yes, Save')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CardShell>
  );
}

// ─── Ana sayfa ──────────────────────────────────────────────────────────────

export default function AdminDanismanAnaSayfa() {
  const { lang } = useLanguage();
  const { authHeaders, user } = useAuth();
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const isDanisman = user?.role === 'danisman';
  const isAdmin = !isDanisman;

  // Mesai başlangıcını sayfa mount olduğunda BİR KEZ tetikle (idempotent
  // backend, ama StrictMode çift-mount'a karşı ref guard). Sonucu sessizce
  // yut — toast gösterme (spec).
  const mesaiTetiklendi = useRef(false);
  useEffect(() => {
    if (mesaiTetiklendi.current) return;
    mesaiTetiklendi.current = true;
    axios.post(`${API}/danisman/cockpit/mesai-basla`, {}, { headers: authHeaders() }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AdminLayout>
      <div className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full" style={{ backgroundColor: 'var(--crm-cream)' }}>
        <PageHeader
          breadcrumbs={[rootBreadcrumb(user?.role, tr), { label: tr('Ana Sayfa', 'Home') }]}
          title={tr('Danışman Ana Sayfası', 'Advisor Home')}
          subtitle={tr('Günlük satış, mesai ve raporlarınızı tek ekrandan yönetin.', 'Manage your daily sales, shift and reports from one screen.')}
        />

        {/* Blok 1 — Günlük/Aylık Danışman Satışları — kısmi maskeleme sunucuda
            yapılır, hem admin hem danışman rolü AYNI listeyi görür (bkz.
            DanismanSatislariPanels.jsx dosya başı yorumu, 2026-08). Her panel
            kendi ErrorBoundary'si içinde — biri patlarsa diğer 5 panel
            etkilenmesin (bkz. proje-yoneticisi görev talimatı 2026-08-13,
            Kusur 2). */}
        <div className="mb-5">
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            {/* showTahsilat={false}: ana sayfa karti CIRO odakli (24 Agustos 2026
                 Recep karari). Tahsilat kirilimi Ciro Dashboard'da duruyor. */}
            <DanismanSatislariCards authHeaders={authHeaders} lang={lang} tr={tr} showTahsilat={false} />
          </ErrorBoundary>
        </div>

        {/* Blok 2 — Bugün Satışlar / Mesai Başlangıç / Değerlendirmeler */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            <BugunSatislarPanel authHeaders={authHeaders} lang={lang} tr={tr} isAdmin={isAdmin} />
          </ErrorBoundary>
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            <MesaiPanel authHeaders={authHeaders} lang={lang} tr={tr} />
          </ErrorBoundary>
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            <DegerlendirmelerPanel tr={tr} />
          </ErrorBoundary>
        </div>

        {/* Blok 3 — Ödemesi Gelenler / Gün Sonu Raporu */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            <OdemesiGelenlerPanel authHeaders={authHeaders} lang={lang} tr={tr} isAdmin={isAdmin} />
          </ErrorBoundary>
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            <GunSonuRaporuPanel authHeaders={authHeaders} lang={lang} tr={tr} isDanisman={isDanisman} />
          </ErrorBoundary>
        </div>
      </div>
    </AdminLayout>
  );
}
